import { useState } from "react";
import type { Movie } from "@/mocks/movies";
import ShareBar from "@/pages/detail/components/ShareBar";

interface DetailHeroProps {
  movie: Movie;
}

export default function DetailHero({ movie }: DetailHeroProps) {
  const [guideOpen, setGuideOpen] = useState(false);

  return (
    <section className="relative overflow-hidden border-b border-background-200 bg-primary-950">
      <div className="absolute inset-0 opacity-25">
        <img
          src={movie.poster}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/70"></div>

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 gap-6 px-4 py-8 md:grid-cols-[280px_minmax(0,1fr)] md:gap-10 md:px-8 md:py-12">
        <div className="mx-auto w-44 shrink-0 overflow-hidden rounded-lg bg-primary-900 md:mx-0 md:w-full">
          <div className="aspect-[2/3] w-full">
            <img
              src={movie.poster}
              alt={`${movie.title}高清电影海报`}
              title={`${movie.title}高清电影海报`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="min-w-0 text-background-50">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 font-label text-xs text-primary-950">
              <i className="ri-hd-line" aria-hidden="true"></i>
              高清影视资源
            </span>
            {movie.genres.map((g) => (
              <span
                key={g}
                className="whitespace-nowrap rounded-full bg-background-50/15 px-3 py-1 font-label text-xs text-background-100"
              >
                {g}
              </span>
            ))}
          </div>

          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight md:text-4xl">{movie.title}</h1>
          <p className="mt-2 font-heading text-sm italic text-accent-300 md:text-base">{movie.tagline}</p>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-sm text-background-200">
            <span className="inline-flex items-center gap-1.5 text-accent-300">
              <i className="ri-star-fill" aria-hidden="true"></i>
              <span className="font-semibold text-background-50">{movie.score.toFixed(1)}</span>
              <span className="text-background-400">/ 10</span>
            </span>
            <span>{movie.year}</span>
            <span>{movie.region}</span>
            <span>{movie.duration}</span>
          </div>

          <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2.5 text-sm sm:grid-cols-2">
            <div className="flex gap-2">
              <dt className="shrink-0 text-background-400">导演</dt>
              <dd className="text-background-100">{movie.director}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 text-background-400">类型</dt>
              <dd className="text-background-100">{movie.genres.join(" / ")}</dd>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <dt className="shrink-0 text-background-400">主演</dt>
              <dd className="text-background-100">{movie.cast.join("、")}</dd>
            </div>
          </dl>

          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200">{movie.excerpt}</p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setGuideOpen(true)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 font-label text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg" aria-hidden="true"></i>
              查看观看指南
            </button>
            <ShareBar movie={movie} />
          </div>

          <p className="mt-4 inline-flex items-center gap-2 text-xs text-background-400">
            <i className="ri-information-line" aria-hidden="true"></i>
            本站为演示站点，暂不提供真实播放与下载功能，以上信息均为虚构演示内容。
          </p>
        </div>
      </div>

      {guideOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/60 px-4 animate-fade-in" role="dialog" aria-modal="true">
          <div className="absolute inset-0" onClick={() => setGuideOpen(false)} aria-hidden="true"></div>
          <div className="relative w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-start justify-between gap-4">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">观看指南说明</h2>
              <button
                type="button"
                onClick={() => setGuideOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" aria-hidden="true"></i>
                本页整理影片的类型、导演、主演与剧情介绍，方便你了解内容后再决定是否观看。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" aria-hidden="true"></i>
                演示站点不接入真实影视数据源，因此不提供在线播放、下载与收藏。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" aria-hidden="true"></i>
                影评区为前端互动演示，提交的评论不会被保存，刷新后即清空。
              </li>
            </ul>
            <button
              type="button"
              onClick={() => setGuideOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </section>
  );
}